import React from 'react';
import {
  Container,
  Stack,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import ConfirmDelete from '../../../components/confirm-delete';
import DocumentList from './document-list';
import AddDocumentsDialog from '../../../components/add-documents-dialog';
import Sidebar from '../sidebar';

type DocsViewProps = {
  docs: Doc[];
  docError: ApiError | undefined;
  isLoading: boolean;
  itineraries: Doc[];
  docToDelete: Doc | null;
  handleDelete: (doc: Doc) => void;
  onCancelDelete: () => void;
  onClickDocument: (document: Doc) => void;
  onConfirmDelete: () => void;
  submissionError: ApiError | undefined;
  travelerId: string | undefined;
  showModal: boolean,
  toggleModal: () => void,
  updateDocs: () => void,
  partner: Partner | undefined;
};

const DocsView = ({
  docs,
  docError,
  isLoading,
  itineraries,
  docToDelete,
  handleDelete,
  onCancelDelete,
  onClickDocument,
  onConfirmDelete,
  submissionError,
  travelerId,
  showModal,
  toggleModal,
  updateDocs,
  partner,
}: DocsViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  if (isMobile) {
    return (
      <Container
        maxWidth="md"
        sx={{
          paddingTop: {
            desktop: 5,
            mobile: 4,
          },
          paddingBottom: {
            desktop: 5,
            mobile: 4.5,
          },
        }}
      >
        <Stack spacing={4}>
          {docError && (
            <Alert severity="error">
              <AlertTitle>There was an issue downloading the document</AlertTitle>
              {docError.message}
              {docError.code && `Code: ${docError.code} `}
              {docError.id && `ID: ${docError.id}`}
            </Alert>
          )}
          {/* General Documents */}
          <Stack
            direction={{ xs: 'column', md: 'row' }} // 'xs' denotes mobile and 'md' and above denotes desktop
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ mb: 4 }}
          >
            <Typography variant="h2">
              Travel Documents
            </Typography>
          </Stack>
          <Stack>
            <DocumentList
              documents={docs}
              isLoading={isLoading}
              onClickDocument={onClickDocument}
              onClickDelete={handleDelete}
              travelerId={travelerId}
              emptyList={(
                <Alert severity="info">
                  <AlertTitle>
                    There are no documents attached to this trip yet.
                  </AlertTitle>
                </Alert>
            )}
            />
            <Button
              data-e2e="ztest-trip-match-docs-upload"
              variant="outlined"
              onClick={toggleModal}
              sx={{
                minWidth: { xs: '100%', md: 'fit-content' },
                mt: { xs: 2, md: 0 }, // Add margin-top for mobile only
              }}
            >
              Upload Document
            </Button>
          </Stack>

          {/* Itinerary Documents */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{ mb: 4 }}
          >
            <Typography variant="h2">
              Itineraries
            </Typography>
          </Stack>
          <DocumentList
            documents={itineraries}
            isLoading={isLoading}
            onClickDocument={onClickDocument}
            onClickDelete={handleDelete}
            travelerId={travelerId}
            emptyList={(
              <Alert severity="info">
                <AlertTitle>
                  There are no itineraries attached to this trip yet.
                </AlertTitle>
              </Alert>
      )}
          />
        </Stack>
        {docToDelete && (
          <ConfirmDelete
            onCancel={onCancelDelete}
            onRemove={onConfirmDelete}
            open={!!docToDelete}
            submissionError={submissionError}
            title="Remove Document?"
            helperText="The document will be permanently deleted and you will no longer have access."
          />
        )}
        <AddDocumentsDialog
          closeDialog={toggleModal}
          onDocumentUploaded={updateDocs}
          open={showModal}
        />
      </Container>
    );
  }
  return (
    <>
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          flex: 1,
          pt: 3.75,
        }}
      >
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            desktop={4}
            sx={{
              display: 'block',
              borderRight: '1px solid #C2C2C2',
            }}
          >
            {/* Sticky Sidebar */}
            <Sidebar partner={partner} />
          </Grid>
          <Grid
            item
            desktop={8}
          >
            <Stack spacing={4} pl={3}>
              {docError && (
              <Alert severity="error">
                <AlertTitle>There was an issue downloading the document</AlertTitle>
                {docError.message}
                {docError.code && `Code: ${docError.code} `}
                {docError.id && `ID: ${docError.id}`}
              </Alert>
              )}
              {/* General Documents */}
              <Stack
                direction={{ xs: 'column', md: 'row' }} // 'xs' denotes mobile and 'md' and above denotes desktop
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ mb: 4 }}
              >
                <Typography variant="h2">
                  Travel Documents
                </Typography>
                <Button
                  data-e2e="ztest-trip-match-docs-upload"
                  variant="outlined"
                  onClick={toggleModal}
                  sx={{
                    minWidth: { xs: '100%', md: 'fit-content' },
                    mt: { xs: 2, md: 0 }, // Add margin-top for mobile only
                  }}
                >
                  Upload Document
                </Button>
              </Stack>
              <DocumentList
                documents={docs}
                isLoading={isLoading}
                onClickDocument={onClickDocument}
                onClickDelete={handleDelete}
                travelerId={travelerId}
                emptyList={(
                  <Alert severity="info">
                    <AlertTitle>
                      There are no documents attached to this trip yet.
                    </AlertTitle>
                  </Alert>
                )}
              />

              {/* Itinerary Documents */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ mb: 4 }}
              >
                <Typography variant="h2">
                  Itineraries
                </Typography>
              </Stack>
              <DocumentList
                documents={itineraries}
                isLoading={isLoading}
                onClickDocument={onClickDocument}
                onClickDelete={handleDelete}
                travelerId={travelerId}
                emptyList={(
                  <Alert severity="info">
                    <AlertTitle>
                      There are no itineraries attached to this trip yet.
                    </AlertTitle>
                  </Alert>
      )}
              />
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {docToDelete && (
      <ConfirmDelete
        onCancel={onCancelDelete}
        onRemove={onConfirmDelete}
        open={!!docToDelete}
        submissionError={submissionError}
        title="Remove Document?"
        helperText="The document will be permanently deleted and you will no longer have access."
      />
      )}
      <AddDocumentsDialog
        closeDialog={toggleModal}
        onDocumentUploaded={updateDocs}
        open={showModal}
      />
    </>
  );
};

export default DocsView;
